import useAxios from 'axios-hooks';
import _ from 'lodash';
import { useState } from 'react';

import { handleFileDownload } from 'components/CommonUtilityServices/DownloadFileService';
import { handlePdfDownload } from 'components/CommonUtilityServices/DownloadPdfService';
import { GET_NEWS, NEWS_ARTICLES } from 'constants/api';

const noop = () => {};

/**
 * Centralizes the news-article/announcement "open" behavior that was duplicated
 * across every view rendering a NewsArticleModal:
 *  - lazily fetches the article body on demand (useAxios)
 *  - opens the modal for inline (extension-less) articles
 *  - streams pdf/doc attachments to a new window instead of opening the modal
 *
 * Spread the returned `newsArticleModalProps` onto a single <NewsArticleModal>
 * rendered once per view (outside of any list/map).
 *
 * @param {object} [options]
 * @param {function} [options.setLoadingFile] - notified while an attachment downloads
 * @param {function} [options.setFileError] - notified with an attachment download error
 */
export const useNewsArticleModal = ({ setLoadingFile = noop, setFileError = noop } = {}) => {
  const [open, setOpen] = useState(false);
  const [{ data, loading, error }, executeNewsReview] = useAxios({}, { manual: true });

  const openNewsArticle = (id, ext) => {
    const trimmedExt = _.trim(ext); // remove whitespace
    if (_.isEmpty(trimmedExt)) {
      setOpen(true);
      executeNewsReview({ url: `${GET_NEWS}${NEWS_ARTICLES}/${id}` });
    } else {
      const fileURL = `${NEWS_ARTICLES}/${id}`;
      if (trimmedExt === 'pdf') {
        handlePdfDownload('GET', null, fileURL, setLoadingFile, setFileError, setFileError, 'newWindow', `${GET_NEWS}`);
      } else {
        handleFileDownload(fileURL, setLoadingFile, setFileError, setFileError, 'newWindow', 'GET', undefined, GET_NEWS);
      }
    }
  };

  const closeNewsArticle = () => setOpen(false);

  const newsArticleModalProps = {
    open,
    handleClose: closeNewsArticle,
    loading,
    error,
    data,
  };

  return { openNewsArticle, closeNewsArticle, newsArticleModalProps };
};

export default useNewsArticleModal;
